import { useParams, Navigate } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    intro: "People, redefined (operating under Amani Talent Agency LLC) respects your privacy. This policy explains how we collect, use, and protect your information.",
    sections: [
      ["Information We Collect", "We collect information you provide directly — such as your name, email, company details, career goals, and any documents you upload — as well as basic usage data needed to operate the site."],
      ["How We Use Information", "We use your information to respond to inquiries, deliver services, process payments, send confirmations, and improve our offerings. We do not sell your personal information."],
      ["Document & File Security", "Resumes and documents you upload are stored privately with access controls and are only accessible to authorized personnel."],
      ["Payments", "Payments are processed securely by Stripe. We never store your full credit card details on our servers."],
      ["Your Choices", "You may request access to, correction of, or deletion of your personal information by contacting us at any time. You may unsubscribe from marketing emails at any time."],
      ["Contact", "For privacy questions, email contact@peopleredefined.com."],
    ],
  },
  terms: {
    title: "Terms of Service",
    intro: "These terms govern your use of the people, redefined website and services.",
    sections: [
      ["Services", "People, redefined provides HR consulting, recruiting, and career services delivered 100% virtually. Specific deliverables are defined in each engagement or package."],
      ["Engagements & Payment", "Fees, scope, and timelines are agreed before work begins. Payments for career service packages may be collected online via secure checkout."],
      ["Professional Scope", "HR compliance guidance is provided within an appropriate professional scope and does not constitute legal advice."],
      ["Client Responsibilities", "Clients agree to provide accurate information and timely materials necessary to deliver services."],
      ["Limitation of Liability", "Services are provided on a best-efforts basis. To the extent permitted by law, our liability is limited to the fees paid for the relevant service."],
      ["Changes", "We may update these terms from time to time. Continued use of the site constitutes acceptance of the current terms."],
    ],
  },
  refund: {
    title: "Refund & Cancellation Policy",
    intro: "We want you to feel confident working with us. Please review our cancellation and refund terms before purchasing.",
    sections: [
      ["Consultations & Sessions", "Appointments may be rescheduled or cancelled using the links provided in your booking confirmation. Please provide at least 24 hours' notice where possible."],
      ["Career Service Packages", "Because career services involve dedicated, personalized work, refunds are considered on a case-by-case basis. Once substantial work has begun, fees are generally non-refundable."],
      ["Deposits", "Where deposits apply, they secure your spot and dedicated time and may be non-refundable."],
      ["Discount Codes", "Discount codes, where offered, are applied at checkout and cannot be combined unless stated."],
      ["How to Request", "To request a cancellation or discuss a refund, email contact@peopleredefined.com with your order details."],
    ],
  },
};

export default function Legal() {
  const { type } = useParams();
  const data = CONTENT[type];
  if (!data) return <Navigate to="/" replace />;

  return (
    <div data-testid={`legal-${type}`}>
      <SEO title={data.title} description={data.intro} />
      <section className="max-w-3xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-24">
        <Reveal>
          <h1 className="font-serif text-5xl md:text-6xl text-espresso">{data.title}</h1>
          <p className="text-espresso/70 mt-6 text-lg leading-relaxed">{data.intro}</p>
        </Reveal>
        <div className="mt-12 space-y-10">
          {data.sections.map(([h, b], i) => (
            <Reveal key={h} delay={i * 0.05}>
              <div>
                <h2 className="font-serif text-2xl text-espresso">{h}</h2>
                <p className="text-espresso/75 mt-3 leading-relaxed">{b}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="text-sm text-espresso/40 mt-14">Last updated {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long" })}.</p>
      </section>
    </div>
  );
}
